'use client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  getApplicationDoctor,
  getDoctorByName,
  getDoctorForSelection
} from './actions';
import { usePathname, useSearchParams } from 'next/navigation';

const ApplicationDoctorContext = createContext(null);

export function useApplicationDoctor() {
  return useContext(ApplicationDoctorContext);
}

const extractLeadIdFromPath = (urlString) => {
  try {
    const url = new URL(urlString, window.location.origin);
    return /00Q[^/]+/.exec(url.pathname)?.[0] || null;
  } catch {
    return null;
  }
};

const extractDoctorSlugFromPath = (urlString) => {
  try {
    const url = new URL(urlString, window.location.origin);
    const parts = url.pathname.split('/').filter(Boolean);

    const slugCandidate = parts[0] === 'applications' ? parts[1] : null;
    if (slugCandidate && !/^00Q[^/]+$/.test(slugCandidate)) {
      return slugCandidate;
    }
    return null;
  } catch {
    return null;
  }
};

export function ApplicationDoctorProvider({ children }) {
  const [doctor, setDoctor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Set once a step picks a provider, so the URL lookup below cannot clear it.
  const selectedRef = useRef(false);

  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Pins a doctor the URL lookup cannot then clear.
  const setSelectedDoctor = useCallback((next) => {
    if (!next) return;
    selectedRef.current = true;
    setDoctor(next);
  }, []);

  const selectDoctorById = useCallback(
    async (doctorId) => {
      if (!doctorId) return;
      try {
        const selected = await getDoctorForSelection(doctorId);
        if (selected) setSelectedDoctor(selected);
      } catch {
        /* leave the current header alone */
      }
    },
    [setSelectedDoctor]
  );

  const clearSelectedDoctor = useCallback(() => {
    selectedRef.current = false;
    setDoctor(null);
  }, []);

  useEffect(() => {
    const callbackParam = searchParams.get('callbackUrl') || '';
    const currentUrl = `${pathname}${window.location.search}`;

    const tryLoadDoctor = async () => {
      setIsLoading(true);
      let doctor = null;
      try {
        const doctorSlug =
          extractDoctorSlugFromPath(currentUrl) ||
          extractDoctorSlugFromPath(callbackParam);
        if (doctorSlug) {
          doctor = await getDoctorByName(doctorSlug);
        }

        if (!doctor) {
          const leadId =
            extractLeadIdFromPath(currentUrl) ||
            extractLeadIdFromPath(callbackParam);
          if (leadId) {
            doctor = await getApplicationDoctor(leadId);
          }
        }

        if (doctor || !selectedRef.current) {
          setDoctor(doctor);
        }
      } catch {
        if (!selectedRef.current) {
          setDoctor(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    tryLoadDoctor();
  }, [pathname, searchParams]);

  return (
    <ApplicationDoctorContext.Provider
      value={{
        doctor,
        isLoading,
        selectDoctorById,
        setSelectedDoctor,
        clearSelectedDoctor
      }}
    >
      {children}
    </ApplicationDoctorContext.Provider>
  );
}
